import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { attendanceApi, type SelfPunchOutcome } from "@/lib/api/attendance";
import { ApiRequestError } from "@/lib/api/client";
import { qk } from "@/lib/query-client";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { captureCurrentLocation, GeolocationCaptureError } from "@/lib/utils/geolocation";
import { businessToday, formatPunchTime, formatWorkedMinutes } from "@/lib/utils/attendance-date";
import { useFeatureEnabled } from "@/lib/features";
import { describeHolidayDay } from "@/lib/attendanceDisplay";
import type { AttendanceRecord } from "@/types/domain";

type PunchType = "check_in" | "check_out";
interface PunchVariables {
  punchType: PunchType;
  /** Only ever sent (and only ever required) for a check-in on an
   * applicable holiday - see the backend's own gate,
   * AttendanceService.SelfPunch. */
  reason?: string;
}

/** "01:23:45" - the live on-break duration counter. Local to this card:
 * nothing else in the app needs a ticking HH:MM:SS clock, unlike
 * formatWorkedMinutes (a settled, server-computed total) or
 * formatDurationMs (trace/query latency) already in lib/utils. */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, "0")).join(":");
}

/** The message shown for a rejected Start/End Break request - same
 * "backend's own message plus a network/generic fallback" shape as
 * rejectionMessage below, but a break has no geo-fence/accuracy detail to
 * add (see AttendanceService.StartBreak/EndBreak - no location is even
 * sent), so the backend's own sentence ("you must check in before starting
 * a break," "you already have an active break," ...) is shown as-is. */
function breakErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.appError.message;
  return error instanceof Error ? error.message : "Could not update your break. Please try again.";
}

/**
 * The message shown for a rejected punch.
 *
 * The backend reports WHY it refused in `error.details.reason`, and the two
 * reasons mean genuinely different things: `low_accuracy` is "we could not
 * measure where you are precisely enough to decide", `outside_geo_fence` is
 * "we did measure you, and you are somewhere else". Showing "accuracy is too
 * low" for both - which is what used to happen, because the backend checked
 * accuracy before it looked at distance at all - told employees standing at
 * their own desk something untrue and gave them nothing to act on.
 *
 * The backend's own sentence is used verbatim; this only adds the measured
 * numbers when it has them, so an employee can see how far out they were and
 * an admin can audit the same figures in the punch log.
 */
function rejectionMessage(error: unknown): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  const details = error.appError.details ?? {};
  const reason = typeof details.reason === "string" ? details.reason : null;
  if (!reason) return null;

  const distance = typeof details.distance_meters === "number" ? Math.round(details.distance_meters) : null;
  const accuracy = typeof details.accuracy_meters === "number" ? Math.round(details.accuracy_meters) : null;

  const parts: string[] = [];
  if (reason === "outside_geo_fence" && distance != null) {
    parts.push(`You are about ${distance}m from your work location.`);
  }
  if (reason === "low_accuracy" && accuracy != null) {
    parts.push(`Your device reported an accuracy of ±${accuracy}m.`);
  }
  return [error.appError.message, ...parts].join(" ");
}

/** Geo-tagged self-service check-in/check-out - a small action card above
 * MyAttendancePage's existing (unchanged) history table, per the geo-tagging
 * spec's "do not redesign the module" instruction.
 *
 * Every tap re-requests the device's CURRENT location and refines a poor
 * first fix before submitting (see captureCurrentLocation) rather than
 * sending whichever coarse reading arrived first. What it then displays -
 * check-in time, check-out time, working time - is read straight out of the
 * canonical attendance record the server returns with the punch, never
 * derived here from timestamps or a timer, so this card and the Tenant
 * Admin's attendance table are showing the same persisted row.
 *
 * `todayAttendance` is optional: the ESS dashboard already fetches today's
 * settled record for its own "Today" card and passes it straight through so
 * this card never re-derives holiday state from a second source of truth;
 * MyAttendancePage (which does not fetch it separately) falls back to the
 * settled record already carried on the Current Break state-restoration
 * read below (breakState.attendance) - both are the exact same
 * ForEmployee-backed, self-healing read on the backend. */
export function SelfCheckInCard({ todayAttendance }: { todayAttendance?: AttendanceRecord } = {}) {
  const queryClient = useQueryClient();
  const today = businessToday();
  const punchesKey = qk.attendance.myPunches({ from: today, to: today });
  const [feedback, setFeedback] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  // The canonical attendance record the last punch settled to. Rendered as-is
  // - the server is the source of truth for check-in, check-out and worked
  // minutes, and this UI does no arithmetic of its own on any of them.
  const [settled, setSettled] = useState<SelfPunchOutcome["attendance"] | null>(null);
  // Geo-Fencing (Phase 3): purely a UX cue here - location is always
  // captured (Phase 0/1's geo-tagging, unconditional), but the "Verifying
  // location..." state and stricter success wording only make sense to show
  // when the backend is actually going to enforce a geo-fence, not merely
  // record coordinates for transparency. The backend enforces regardless of
  // what this flag reads (see AttendanceService.validateGeoFence) - this
  // never gates the request itself, only the wording.
  const geoFencingEnabled = useFeatureEnabled("geo_fencing");

  const { data: todaysPunches, isLoading } = useQuery({
    queryKey: punchesKey,
    queryFn: () => attendanceApi.myPunches({ from: today, to: today, per_page: 20 }),
  });

  const lastDirection = todaysPunches?.items[todaysPunches.items.length - 1]?.direction;

  // Break System: state restoration (section 21/22) - the card always
  // reflects what the backend actually has, never what a prior render
  // assumed. A page refresh, a second tab, or simply reopening the app
  // picks this query back up and shows the SAME active break, if any.
  const currentBreakKey = ["attendance", "current-break"] as const;
  const { data: breakState, isLoading: breakStateLoading } = useQuery({
    queryKey: currentBreakKey,
    queryFn: () => attendanceApi.currentBreak(),
  });
  const activeBreak = breakState?.break ?? null;
  // Yesterday's session still open after midnight: today's punches cannot show
  // it, so it comes from the server. Check Out closes it; a Check In would
  // leave it without a check-out, so that is not offered until this has loaded.
  const overnight = breakState?.overnight_session ?? null;
  const hasOpenSession = lastDirection === "in" || overnight != null;
  const primaryAction: PunchType = hasOpenSession ? "check_out" : "check_in";
  const { data: myMode } = useQuery({
    queryKey: ["attendance", "my-attendance-mode"],
    queryFn: () => attendanceApi.myAttendanceMode(),
    // Re-read on every mount so an admin's change shows on the next look.
    staleTime: 0,
  });
  // Approved-leave gate (AttendanceService.resolveLeaveEligibility), carried
  // on this same state-restoration read - see currentBreak's own doc
  // comment. `hasOpenSession` uses the raw punch list, not the settled
  // record: a full-day leave day settles with first_in/last_out cleared
  // (engine.go's own leave branch), but the raw check-in punch that
  // predates the approval is still there and still needs to be closed.
  const leaveEligibility = breakState?.leave_eligibility;
  const onApprovedLeave = leaveEligibility?.on_approved_leave === true;
  const [breakFeedback, setBreakFeedback] = useState<{ tone: "success" | "error"; message: string } | null>(null);

  // Holiday-aware attendance: prefer the caller's own already-fetched
  // record (the ESS dashboard's "Today" card), fall back to the one
  // carried on the break state-restoration read - see this component's own
  // doc comment. `unworked` is the ONLY state a fresh check-in can still
  // change: `working`/`worked` mean the employee already opened (or
  // closed) the day, so the reason was already captured then.
  const holidayInfo = describeHolidayDay(overnight ? breakState?.attendance : (todayAttendance ?? breakState?.attendance));
  const isHolidayCheckIn = holidayInfo?.state === "unworked";
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonDraft, setReasonDraft] = useState("");

  // The live "Break Duration: 00:27:42" counter - ticks only while a break
  // is actually active, and stops (no stray interval) the moment it isn't.
  // Depends on the primitive start_time (not the `activeBreak` object
  // reference), so a refetch that resolves to the SAME active break doesn't
  // reset the running interval.
  const activeBreakStart = activeBreak?.start_time;
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!activeBreakStart) return;
    const started = new Date(activeBreakStart).getTime();
    setElapsedMs(Date.now() - started);
    const interval = setInterval(() => setElapsedMs(Date.now() - started), 1000);
    return () => clearInterval(interval);
  }, [activeBreakStart]);

  const startBreak = useMutation({
    mutationFn: () => attendanceApi.startBreak(),
    onMutate: () => setBreakFeedback(null),
    onSuccess: async (outcome) => {
      setBreakFeedback(null);
      if (outcome.attendance) setSettled(outcome.attendance);
      await queryClient.invalidateQueries({ queryKey: currentBreakKey });
    },
    onError: (err) => setBreakFeedback({ tone: "error", message: breakErrorMessage(err) }),
  });

  const endBreak = useMutation({
    mutationFn: () => attendanceApi.endBreak(),
    onMutate: () => setBreakFeedback(null),
    onSuccess: async (outcome) => {
      if (outcome.attendance) setSettled(outcome.attendance);
      const range =
        outcome.break.end_time != null
          ? `${formatPunchTime(outcome.break.start_time)} - ${formatPunchTime(outcome.break.end_time)}`
          : formatPunchTime(outcome.break.start_time);
      setBreakFeedback({
        tone: "success",
        message: `Break Completed. ${range}. Duration: ${formatWorkedMinutes(outcome.break.duration_minutes)}. Status: Working.`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: currentBreakKey }),
        queryClient.invalidateQueries({ queryKey: punchesKey, refetchType: "active" }),
      ]);
    },
    onError: (err) => setBreakFeedback({ tone: "error", message: breakErrorMessage(err) }),
  });

  // Start Break is only offered once actually checked in for today, not
  // already on break, and not on an approved full-day leave (section 4's
  // own pre-conditions, plus the leave gate - mirrored here purely as UX,
  // the backend enforces all of it regardless of what this hides). End
  // Break is offered whenever an active break exists, even one that started
  // on an earlier day the employee forgot to close, or on an approved-leave
  // day - closing something that already exists is always permitted.
  const canStartBreak = hasOpenSession && !activeBreak && !onApprovedLeave;
  // A NEW check-in is never offered on an approved full-day leave date. A
  // checkout remains offered exactly when there is an existing open session
  // to close (lastDirection === "in"), on-leave or not - see
  // AttendanceService.SelfPunch's own gate for why only PunchIn is ever
  // blocked. With no open session at all, neither button is shown while on
  // leave (requirement: no attendance action buttons at all in that state).
  // An admin can turn self check-in off (attendance mode check_in_off):
  // no Check In button, but an open session can still be checked out.
  const checkInOff = myMode?.mode === "check_in_off";
  const showCheckIn = !onApprovedLeave && !checkInOff && !overnight && !breakStateLoading;
  const showCheckOut = !onApprovedLeave || hasOpenSession;

  const mark = useMutation<SelfPunchOutcome, unknown, PunchVariables>({
    mutationFn: async ({ punchType, reason }) => {
      const coords = await captureCurrentLocation();
      const submit = punchType === "check_in" ? attendanceApi.checkIn : attendanceApi.checkOut;
      return submit({
        latitude: coords.latitude,
        longitude: coords.longitude,
        // A device that reported no usable accuracy sends none at all rather
        // than a fabricated number - the backend treats a missing accuracy
        // as "unknown" and falls back to the distance check.
        accuracy_meters: Number.isFinite(coords.accuracy) ? coords.accuracy : undefined,
        reason,
      });
    },
    onMutate: () => setFeedback(null),
    onSuccess: async (outcome, { punchType }) => {
      setSettled(outcome.attendance ?? null);
      // location_verified is only ever true when Geo-Fencing actually
      // enforced a match (Phase 3) - a plain informational capture (Phase
      // 0/1, or Geo-Fencing disabled) never sets it, so this reflects real
      // backend behaviour rather than the client's own toggle read.
      const verified = outcome.location_verified === true;
      const action = punchType === "check_in" ? "Checked in" : "Checked out";
      // An idempotent replay (double tap, retried request) is a success, not
      // an error: the punch it repeats is already recorded, and the record
      // returned above is the real one.
      const at = formatPunchTime(outcome.punch_time);
      const message = outcome.duplicate
        ? `${action} at ${at} — already recorded.`
        : verified
          ? `Location verified. ${action} at ${at}.`
          : `${action} at ${at} — location captured.`;
      setFeedback({ tone: "success", message });
      // Refetch rather than merely invalidate, so the punch list and every
      // other attendance view are showing server state before this handler
      // returns - no reload, no arbitrary delay.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: punchesKey, refetchType: "active" }),
        queryClient.invalidateQueries({ queryKey: ["attendance"], refetchType: "active" }),
        // A check-out settles the day, which is what the My Earnings card counts.
        queryClient.invalidateQueries({ queryKey: ["earnings"], refetchType: "active" }),
      ]);
    },
    onError: (err) => {
      if (err instanceof GeolocationCaptureError) {
        setFeedback({ tone: "error", message: err.message });
        return;
      }
      // Check-in was turned off after this page loaded: re-read the mode so
      // the Check In button goes away straight after the refusal.
      if (err instanceof ApiRequestError && err.appError.details?.reason === "check_in_off") {
        void queryClient.invalidateQueries({ queryKey: ["attendance", "my-attendance-mode"] });
      }
      const rejected = rejectionMessage(err);
      if (rejected) {
        setFeedback({ tone: "error", message: rejected });
        return;
      }
      const message = err instanceof Error ? err.message : "Could not record attendance. Please try again.";
      setFeedback({ tone: "error", message });
    },
  });

  const pending = mark.isPending ? mark.variables?.punchType : undefined;

  const submitHolidayCheckIn = () => {
    const reason = reasonDraft.trim();
    if (!reason) return;
    setReasonModalOpen(false);
    mark.mutate({ punchType: "check_in", reason });
    setReasonDraft("");
  };

  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-800">
              {onApprovedLeave
                ? "On Leave"
                : activeBreak
                  ? "On Break"
                  : holidayInfo
                    ? holidayInfo.label
                    : hasOpenSession
                      ? "Checked In · Working"
                      : checkInOff
                        ? "Check-in turned off"
                        : "Mark attendance"}
            </p>
            <p className="text-xs text-slate-500">
              {onApprovedLeave
                ? (leaveEligibility?.message ?? "You are on approved leave today.") +
                  (hasOpenSession
                    ? ` You have an open check-in - ${activeBreak ? "end your break, then check out" : "check out"} to close it.`
                    : "")
                : checkInOff && !hasOpenSession
                  ? "Check-in from the app and web is turned off for you. Mark attendance on the biometric device, or contact HR."
                  : mark.isPending
                  ? "📍 Getting an accurate location…"
                  : activeBreak
                    ? `☕ Break Started: ${formatPunchTime(activeBreak.start_time)} · Break Duration: ${formatElapsed(elapsedMs)}`
                    : overnight
                      ? `Checked in since yesterday at ${formatPunchTime(overnight.check_in_at)}.`
                    : geoFencingEnabled
                      ? "📍 Your location will be verified against your work location."
                      : "📍 Your current location will be captured for attendance transparency."}
            </p>
            {/* Half-day leave never restricts anything above - only an
                indicator, since the working portion of the day is ordinary
                attendance (see canStartBreak/showCheckIn/showCheckOut, all
                gated on on_approved_leave, never half_day). */}
            {!onApprovedLeave && leaveEligibility?.half_day && (
              <p className="text-xs text-amber-600">{leaveEligibility.message}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {showCheckIn && (
              <Button
                variant={primaryAction === "check_in" ? "primary" : "outline"}
                size="sm"
                loading={pending === "check_in"}
                disabled={mark.isPending}
                onClick={() => (isHolidayCheckIn ? setReasonModalOpen(true) : mark.mutate({ punchType: "check_in" }))}
              >
                {isHolidayCheckIn ? "Work on Holiday" : "Check In"}
              </Button>
            )}
            {canStartBreak && (
              <Button
                variant="outline"
                size="sm"
                loading={startBreak.isPending}
                disabled={startBreak.isPending || endBreak.isPending || mark.isPending}
                onClick={() => startBreak.mutate()}
              >
                Start Break
              </Button>
            )}
            {activeBreak && (
              <Button
                variant="outline"
                size="sm"
                loading={endBreak.isPending}
                disabled={startBreak.isPending || endBreak.isPending}
                onClick={() => endBreak.mutate()}
              >
                End Break
              </Button>
            )}
            {showCheckOut && (
              <Button
                variant={primaryAction === "check_out" ? "primary" : "outline"}
                size="sm"
                loading={pending === "check_out"}
                disabled={mark.isPending || Boolean(activeBreak)}
                title={activeBreak ? "You are currently on break - end your break before checking out." : undefined}
                onClick={() => mark.mutate({ punchType: "check_out" })}
              >
                Check Out
              </Button>
            )}
          </div>
        </div>

        <Modal
          open={reasonModalOpen}
          onClose={() => setReasonModalOpen(false)}
          title="Work on Holiday"
          description={`Today is ${holidayInfo?.holidayName ?? "a holiday"}. Check-in is not required - tell us why you're working today.`}
          size="sm"
          footer={
            <>
              <Button variant="outline" size="sm" onClick={() => setReasonModalOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={!reasonDraft.trim()} onClick={submitHolidayCheckIn}>
                Check In
              </Button>
            </>
          }
        >
          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-slate-700">Work reason</span>
            <textarea
              className="w-full rounded-lg border border-slate-200 p-2.5 text-sm focus:border-primary-400 focus:outline-none"
              rows={3}
              autoFocus
              placeholder="e.g. covering an urgent client escalation"
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
            />
          </label>
        </Modal>

        {feedback && (
          <p className={feedback.tone === "success" ? "text-sm text-emerald-600" : "text-sm text-red-600"}>
            {feedback.tone === "success" ? "✓ " : "✕ "}
            {feedback.message}
          </p>
        )}

        {breakFeedback && (
          <p className={breakFeedback.tone === "success" ? "text-sm text-emerald-600" : "text-sm text-red-600"}>
            {breakFeedback.tone === "success" ? "✓ " : "✕ "}
            {breakFeedback.message}
          </p>
        )}

        {settled && (
          <dl className="grid grid-cols-3 gap-3 rounded-lg bg-slate-50 p-3 text-sm">
            <div>
              <dt className="text-xs text-slate-500">Check-in</dt>
              <dd className="font-medium text-slate-800">{formatPunchTime(settled.first_in)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Check-out</dt>
              <dd className="font-medium text-slate-800">{formatPunchTime(settled.last_out)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Working time</dt>
              <dd className="font-medium text-slate-800">{formatWorkedMinutes(settled.worked_minutes)}</dd>
            </div>
          </dl>
        )}

        {!isLoading && todaysPunches && todaysPunches.items.length > 0 && (
          <ul className="divide-y divide-slate-100 border-t border-slate-100 pt-2">
            {todaysPunches.items.map((p) => (
              <li key={p.id} className="flex items-center justify-between py-1.5 text-sm">
                <span className="text-slate-700">
                  {p.direction === "in" ? "Check-In" : p.direction === "out" ? "Check-Out" : "Punch"}{" "}
                  <span className="text-slate-400">{formatPunchTime(p.punch_time)}</span>
                </span>
                <span className="text-xs text-slate-500">
                  {p.latitude != null && p.longitude != null
                    ? (<>
                        {p.geo_fence_id ? "✓ Verified" : "📍 Location Captured"}
                        {p.accuracy_meters != null ? ` · Accuracy: ${Math.round(p.accuracy_meters)}m` : ""}
                        {p.source === "field_work" && <span className="ml-1 rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700">Field Work</span>}
                        {" · "}
                        <a
                          href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          View on Maps
                        </a>
                      </>)
                    : <span>Location not captured{p.source === "field_work" && <span className="ml-1 rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700">Field Work</span>}</span>
                  }
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
